import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, FieldLabel, FieldDescription } from '@/components/ui/field';
import { FileText, Shield } from 'lucide-react';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { SubmitButton } from '@/components/ui/app/submit-button';
import { useTranslation } from 'react-i18next';
import i18n from '@/lib/i18n';

function getAccountTypes() {
    return [
        { value: 'individuals', label: i18n.t('account:particuliers'), apiKey: 'user' },
        { value: 'companies', label: i18n.t('account:entreprises'), apiKey: 'company' },
    ];
}

const DEFAULT_FORM_STATE = {
    cguUrl: '',
    privacyPolicyUrl: '',
};

function apiParamsToForm(params) {
    if (!params) return { ...DEFAULT_FORM_STATE };
    return {
        cguUrl: params.cgu_url ?? '',
        privacyPolicyUrl: params.privacy_policy_url ?? '',
    };
}

function formToApiPayload(form) {
    return {
        cgu_url: form.cguUrl || null,
        privacy_policy_url: form.privacyPolicyUrl || null,
    };
}

function AppManagementForm({ state, onChange }) {
    const { t } = useTranslation();
    return (
        <div className="space-y-5">
            {/* CGU */}
            <div className="space-y-4 rounded-[12px] p-5 border border-border">
                <div className="flex items-center gap-3">
                    <div className="bg-muted rounded-full h-9 w-9 flex items-center justify-center shrink-0">
                        <FileText className="h-5 w-5 text-foreground" />
                    </div>
                    <h3 className="text-sm font-semibold text-foreground">{t('account:conditionsGeneralesDUtilisation')}</h3>
                </div>

                <Field>
                    <FieldLabel className="text-[13px] font-medium">{t('account:lienVersVosCguPersonnalisees')}</FieldLabel>
                    <Input
                        type="url"
                        placeholder="https://votreentreprise.com/cgu"
                        value={state.cguUrl}
                        onChange={(e) => onChange({ ...state, cguUrl: e.target.value })}
                    />
                    <FieldDescription className="text-xs">
                        {t('account:ceLienSeraAfficheDansLApplicationLaissez')}
                    </FieldDescription>
                </Field>
            </div>

            {/* Politique de confidentialité */}
            <div className="space-y-4 rounded-[12px] p-5 border border-border">
                <div className="flex items-center gap-3">
                    <div className="bg-muted rounded-full h-9 w-9 flex items-center justify-center shrink-0">
                        <Shield className="h-5 w-5 text-foreground" />
                    </div>
                    <h3 className="text-sm font-semibold text-foreground">{t('account:politiqueDeConfidentialite')}</h3>
                </div>

                <Field>
                    <FieldLabel className="text-[13px] font-medium">
                        {t('account:lienVersVotrePolitiqueDeConfidentialite')}
                    </FieldLabel>
                    <Input
                        type="url"
                        placeholder="https://votreentreprise.com/confidentialite"
                        value={state.privacyPolicyUrl}
                        onChange={(e) => onChange({ ...state, privacyPolicyUrl: e.target.value })}
                    />
                    <FieldDescription className="text-xs">
                        {t('account:ceLienSeraAfficheDansLApplicationLaissez2')}
                    </FieldDescription>
                </Field>
            </div>
        </div>
    );
}

export default function AppManagementContent() {
    const { t } = useTranslation();
    const ACCOUNT_TYPES = getAccountTypes();
    const [accountType, setAccountType] = useState('individuals');
    const [individualsForm, setIndividualsForm] = useState({ ...DEFAULT_FORM_STATE });
    const [companiesForm, setCompaniesForm] = useState({ ...DEFAULT_FORM_STATE });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        horodatyApi.adminParameters
            .get()
            .then((res) => {
                const data = res.data?.data ?? {};
                if (data.user) setIndividualsForm(apiParamsToForm(data.user));
                if (data.company) setCompaniesForm(apiParamsToForm(data.company));
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const currentTab = ACCOUNT_TYPES.find((t) => t.value === accountType);
    const currentForm = accountType === 'individuals' ? individualsForm : companiesForm;
    const setCurrentForm = accountType === 'individuals' ? setIndividualsForm : setCompaniesForm;

    function handleSave() {
        if (!currentTab) return;
        setSaving(true);
        horodatyApi.adminParameters
            .update(currentTab.apiKey, formToApiPayload(currentForm))
            .catch(() => {})
            .finally(() => setSaving(false));
    }

    return (
        <div className="space-y-5">
            {/* Page Header */}
            <div>
                <h2 className="text-base font-semibold text-foreground">{t('account:gestionDeLApplication')}</h2>
                <p className="text-[13px] text-muted-foreground mt-0.5">
                    {t('account:configurezLesParametresGlobauxDeLApplication')}
                </p>
            </div>

            {/* Account Type Switcher */}
            <div className="bg-surface rounded-xl p-1 flex gap-1 w-full max-w-[320px]">
                {ACCOUNT_TYPES.map((tab) => (
                    <Button
                        key={tab.value}
                        variant="plain"
                        color="neutral"
                        size="xlg"
                        onClick={() => setAccountType(tab.value)}
                        className={cn(
                            'flex-1 text-[13px]',
                            accountType === tab.value
                                ? 'bg-card text-foreground shadow-sm hover:bg-card'
                                : 'text-muted-foreground',
                        )}
                    >
                        {tab.label}
                    </Button>
                ))}
            </div>

            {/* Tab Content */}
            {!loading && <AppManagementForm state={currentForm} onChange={setCurrentForm} />}

            {/* Save Button */}
            <div className="flex justify-end">
                <SubmitButton onClick={handleSave} disabled={saving || loading}>
                    {saving ? t('account:enregistrement') : t('account:enregistrer')}
                </SubmitButton>
            </div>
        </div>
    );
}
